/**
 * Msg 595 — Custom SVG icons that Lucide doesn't ship. Each component
 * mimics Lucide's stroke style (24×24, currentColor stroke, no fill,
 * stroke-width 2, round joins) so they blend into the picker grid
 * and the tree rows identically to real Lucide icons.
 *
 * Add a new custom icon: define a `React.forwardRef` component below,
 * then register the (name, component) pair in `topicIconMap.js`.
 */
import React from "react";


const svgProps = {
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};


/**
 * Beach — sun in the top-right, three ripple waves, a dune/sand
 * horizon at the bottom. Kept minimal so it reads well at 14px.
 */
export const Beach = React.forwardRef(function Beach({ className = "", ...rest }, ref) {
  return (
    <svg ref={ref} className={`lucide lucide-beach ${className}`} {...svgProps} {...rest}>
      {/* sun */}
      <circle cx="18" cy="5" r="2" />
      <path d="M18 1v1" />
      <path d="M18 8v1" />
      <path d="M14.5 5h-1" />
      <path d="M22.5 5h-1" />
      {/* two ripple waves */}
      <path d="M2 11c1.5-1.2 3-1.2 4.5 0S9 12.2 10.5 11 13 9.8 14.5 11s3 1.2 4.5 0 3-1.2 4.5 0" />
      <path d="M2 15c1.5-1.2 3-1.2 4.5 0S9 16.2 10.5 15 13 13.8 14.5 15s3 1.2 4.5 0 3-1.2 4.5 0" />
      {/* dune / sand horizon */}
      <path d="M2 21c3-2 6-2 10 0s7 2 10 0" />
    </svg>
  );
});


/** Wheelchair — round wheel, seated figure. */
export const Wheelchair = React.forwardRef(function Wheelchair({ className = "", ...rest }, ref) {
  return (
    <svg ref={ref} className={`lucide lucide-wheelchair ${className}`} {...svgProps} {...rest}>
      {/* head */}
      <circle cx="12" cy="4" r="1.6" />
      {/* torso down to seat */}
      <path d="M12 6v6" />
      {/* arm extending forward */}
      <path d="M12 8l4 2" />
      {/* legs / footrest */}
      <path d="M12 12l4 4" />
      <path d="M12 12h-3" />
      {/* wheel */}
      <circle cx="10" cy="18" r="4" />
      {/* wheel spokes */}
      <path d="M10 14v8" />
      <path d="M6 18h8" />
    </svg>
  );
});


/** Passport — book with stripe and a small dot (photo). */
export const Passport = React.forwardRef(function Passport({ className = "", ...rest }, ref) {
  return (
    <svg ref={ref} className={`lucide lucide-passport ${className}`} {...svgProps} {...rest}>
      {/* outer cover */}
      <rect x="4" y="3" width="16" height="18" rx="2" />
      {/* stripe / spine detail */}
      <path d="M8 3v18" />
      {/* photo circle */}
      <circle cx="14" cy="10" r="2" />
      {/* signature lines */}
      <path d="M12 15h6" />
      <path d="M12 18h6" />
    </svg>
  );
});


/**
 * Volcano — mountain silhouette with a chunky smoke plume from the
 * crater. Uses a truncated triangle so the crater reads at small
 * sizes.
 */
export const Volcano = React.forwardRef(function Volcano({ className = "", ...rest }, ref) {
  return (
    <svg ref={ref} className={`lucide lucide-volcano ${className}`} {...svgProps} {...rest}>
      {/* mountain sides + flat crater */}
      <path d="M3 20l6-9h6l6 9" />
      <path d="M9 11h6" />
      {/* smoke plume */}
      <path d="M10 9c0-1.5 1-2 2-2s2 .5 2 2" />
      <path d="M9 6c0-1 .8-2 2-2" />
      <path d="M14 5c.8-.7 2-.5 2 .6" />
      {/* lava drips down one side */}
      <path d="M11 13v3" />
      <path d="M13 14v2" />
    </svg>
  );
});
